<template>
  <div class="sf-el phone-input">
    <div class="sf-phone">
      <label class="sf-el__label">
        {{ label }}
        <span v-if="required" class="sf-el__required">*</span>
      </label>
      <p v-if="caption" class="sf-el__caption">{{ caption }}</p>
      <input
        ref="phoneInput"
        type="tel"
        autocomplete="tel"
        :placeholder="placeholder"
        class="sf-phone-input"
        @input="handleManualInput"
      />
    </div>
  </div>
</template>

<script>
  import { ref, onMounted, onUnmounted, watch, nextTick, inject } from 'vue';

  export default {
    name: 'PhoneElement',
    props: {
      label: String,
      caption: String,
      name: String,
      modelValue: String,
      required: Boolean,
      placeholder: String,
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
      const phoneInput = ref(null);
      let iti = null;

      // Try to get i18n from Nuxt (might not be available in all contexts)
      let currentLocale = 'nl-NL';
      try {
        // Try Nuxt i18n
        const i18n = inject('i18n', null);
        if (i18n?.locale?.value) {
          currentLocale = i18n.locale.value;
        }
      } catch (e) {
        // Fallback to nl-NL
      }

      // Map locale to country code
      const getCountryFromLocale = (locale) => {
        const localeMap = {
          'nl-NL': 'nl',
          nl: 'nl',
          'en-EN': 'gb',
          'en-GB': 'gb',
          'en-US': 'us',
          en: 'gb',
          'de-DE': 'de',
          de: 'de',
          'fr-FR': 'fr',
          fr: 'fr',
          'be-BE': 'be',
          be: 'be',
          'es-ES': 'es',
          es: 'es',
          'it-IT': 'it',
          it: 'it',
        };
        return localeMap[locale] || 'nl';
      };

      // Get preferred countries based on locale
      const getPreferredCountries = (locale) => {
        const initialCountry = getCountryFromLocale(locale);
        // Put initial country first, then common European countries
        const preferred = [initialCountry, 'nl', 'be', 'de', 'fr', 'gb', 'us'];
        // Remove duplicates while preserving order
        return [...new Set(preferred)];
      };

      const initialCountry = getCountryFromLocale(currentLocale);
      const preferredCountries = getPreferredCountries(currentLocale);

      // Fallback for when intl-tel-input isn't loaded yet
      const handleManualInput = (event) => {
        if (!iti) {
          emit('update:modelValue', event.target.value);
        }
      };

      onMounted(async () => {
        // onMounted only runs in browser, but double check for safety
        if (!phoneInput.value) {
          console.error('Phone input ref not found');
          return;
        }

        // Show any prefilled value while intl-tel-input is still loading / if it fails
        if (props.modelValue) {
          phoneInput.value.value = props.modelValue;
        }

        try {
          // Dynamic import for better code splitting (client-only)
          const intlTelInputModule = await import('intl-tel-input');
          const intlTelInput = intlTelInputModule.default;

          // Import CSS
          await import('intl-tel-input/build/css/intlTelInput.css');

          // Initialize intl-tel-input with locale-based settings
          iti = intlTelInput(phoneInput.value, {
            initialCountry: initialCountry,
            preferredCountries: preferredCountries,
            separateDialCode: false,
            autoPlaceholder: 'polite',
            // Don't reformat the visible input while the user types. We still
            // emit the E.164 number (+31...) via iti.getNumber() for the backend
            // and validation, but the field shows the value as the user typed it.
            formatOnDisplay: false,
            // Show the example number in national format (e.g. 06 12345678)
            // instead of the default international one (+31 6 12345678).
            customPlaceholder: (selectedCountryPlaceholder, selectedCountryData) => {
              const utils = intlTelInput.utils;
              const iso2 = selectedCountryData?.iso2;
              if (utils?.getExampleNumber && iso2) {
                try {
                  const nationalExample = utils.getExampleNumber(
                    iso2,
                    true,
                    utils.numberType.MOBILE
                  );
                  if (nationalExample) {
                    return nationalExample;
                  }
                } catch (e) {
                  // Fall back to the default placeholder below
                }
              }
              return selectedCountryPlaceholder;
            },
            utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@24/build/js/utils.js',
          });

          // Set initial value if provided
          if (props.modelValue) {
            iti.setNumber(props.modelValue);
          }

          // Listen for changes
          phoneInput.value.addEventListener('blur', handlePhoneChange);
          phoneInput.value.addEventListener('countrychange', handlePhoneChange);

          // Remove the manual input handler now that intl-tel-input is active
          phoneInput.value.removeEventListener('input', handleManualInput);
          phoneInput.value.addEventListener('input', handlePhoneChange);
        } catch (error) {
          console.error('Failed to initialize intl-tel-input:', error);
          // Continue with fallback (manual input handler stays active)
        }
      });

      const handlePhoneChange = () => {
        if (iti) {
          // Get the full international number
          const fullNumber = iti.getNumber();
          emit('update:modelValue', fullNumber);
        }
      };

      // Watch for external changes to modelValue
      watch(
        () => props.modelValue,
        (newValue) => {
          if (iti && newValue !== iti.getNumber()) {
            iti.setNumber(newValue || '');
          }
        }
      );

      onUnmounted(() => {
        if (iti && phoneInput.value) {
          phoneInput.value.removeEventListener('blur', handlePhoneChange);
          phoneInput.value.removeEventListener('countrychange', handlePhoneChange);
          phoneInput.value.removeEventListener('input', handlePhoneChange);
          iti.destroy();
        }
      });

      return {
        phoneInput,
        handleManualInput,
      };
    },
  };
</script>

<style scoped lang="scss">
  .sf-phone {
    position: relative;
  }

  :deep(.iti) {
    width: 100%;
  }
  :deep(.iti__country-name) {
    color: var(--cs-choice-default-text);
  }
  :deep(.iti--inline-dropdown .iti__dropdown-content) {
    background-color: var(--cs-choice-default-background);
    border-color: var(--cs-choice-default-border);
  }
</style>
